/**
 * Tabs — Radix-based tab system with a vertical "settings-style" variant.
 *
 * Tablet-first invariants (UX Blueprint §9):
 *   - Trigger min-height 56px (default `md`) / 64px (`lg` for critical flows)
 *   - Visible focus ring
 *   - Color + icon on active state (not color-only)
 *   - Works with touch + keyboard (arrow keys via Radix)
 *
 * Shape:
 *   <Tabs orientation="vertical">
 *     <TabsList>...</TabsList>
 *     <TabsContent value="...">...</TabsContent>
 *   </Tabs>
 */
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import { cn } from '../utils/cn';

export const Tabs = TabsPrimitive.Root;

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    orientation?: 'horizontal' | 'vertical';
  }
>(({ className, orientation = 'horizontal', ...rest }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      orientation === 'vertical'
        ? 'flex flex-col gap-1'
        : 'flex flex-row items-center gap-1 border-b border-biz-border',
      className,
    )}
    {...rest}
  />
));
TabsList.displayName = 'TabsList';

export interface TabsTriggerProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  icon?: ReactNode;
  description?: string;
  orientation?: 'horizontal' | 'vertical';
}

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, icon, description, orientation = 'horizontal', children, ...rest }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'group inline-flex items-center gap-biz-content-lg',
      'min-h-biz-touch px-biz-content-lg rounded-biz-btn',
      'text-biz-sm font-medium text-biz-foreground',
      'transition-colors duration-biz-fast',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
      'disabled:opacity-50 disabled:pointer-events-none',
      orientation === 'vertical'
        ? 'w-full justify-start text-left'
        : 'justify-center',
      'hover:bg-biz-muted',
      'data-[state=active]:bg-biz-primary data-[state=active]:text-biz-primary-foreground',
      className,
    )}
    {...rest}
  >
    {icon ? (
      <span aria-hidden="true" className="shrink-0">
        {icon}
      </span>
    ) : null}
    <span className="flex flex-col items-start leading-tight">
      <span className="truncate">{children}</span>
      {description ? (
        <span
          className={cn(
            'text-biz-xs font-normal',
            'text-biz-muted-foreground group-data-[state=active]:text-biz-primary-foreground/80',
          )}
        >
          {description}
        </span>
      ) : null}
    </span>
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...rest }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded-biz-card',
      className,
    )}
    {...rest}
  />
));
TabsContent.displayName = 'TabsContent';
